import { useState } from 'react'
import { Link, useForm } from '@inertiajs/react'

import { Button } from '@atoms'
import Badge from '@components/atoms/Badge'
import { Card, CardHeading } from '@components/imports/Card'
import { CsvBoxNotSetUpAlert } from '@components/imports/CsvBoxNotSetUpAlert'
import { SupporterImportCsvBox } from '@components/imports/SupporterImportCsvBox'
import { PageLayout } from '@components/layouts/PageLayout'
import { SelectField } from '@components/molecules/SelectField'
import { PageHeaderNav, type PageTabs } from '@molecules/PageHeaderNav'
import Steps from '@components/molecules/Steps'
import cn from '@components/utils/cn'
import Icon, { type IconTypes } from '@components/utils/Icon'
import { type CsvBoxColumn, type CsvBoxEnvironment } from '@utils/imports/types'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('supporter_imports')

export type ImportGroup = { id: number; name: string }

export type DraftImport = { id: number; groupId: number; groupName: string; token: string }

export type SupporterImportsIndexProps = {
  requestingUserId: number
  groups: ImportGroup[]
  draftImport: DraftImport | null
  licenseKey: string | null
  csvBoxEnvironment: CsvBoxEnvironment
  columns: CsvBoxColumn[]
  pageTabs?: PageTabs | null
}

const ROOT_ID = 'supporter-imports'

const PREPARE = 0
const UPLOAD = 1
const CONFIRM = 2

const SEARCHABLE_FROM = 10

const CREATE_IMPORT_PATH = '/imports/supporters'

function CheckList({ items }: { items: Array<{ key: string; title?: string; body: string }> }) {
  return (
    <ul className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100 tw:px-5">
      {items.map((item) => (
        <li key={item.key} className="tw:flex tw:items-start tw:gap-3 tw:py-3">
          <Icon type="check-circle" className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-success-600" aria-hidden />
          <div className="tw:flex tw:flex-col tw:gap-0.5 tw:text-sm">
            {item.title && <span className="tw:font-medium tw:text-gray-cool-900">{item.title}</span>}
            <span className={item.title ? 'tw:text-gray-cool-600' : 'tw:text-gray-cool-700'}>{item.body}</span>
          </div>
        </li>
      ))}
    </ul>
  )
}

function GroupCard({
  groups,
  groupId,
  onChange,
  error,
}: {
  groups: ImportGroup[]
  groupId: string
  onChange: (groupId: string) => void
  error?: string
}) {
  return (
    <Card>
      <CardHeading icon="users-01" title={t('group.title')} subtitle={t('group.subtitle')} />
      <div className="tw:p-5">
        <SelectField
          name="action_group_id"
          label={t('group.label')}
          placeholder={t('group.placeholder')}
          options={groups.map((group) => ({ value: String(group.id), label: group.name }))}
          value={groupId}
          onValueChange={onChange}
          errors={error ? { action_group_id: { type: 'server', message: error } } : undefined}
          search={groups.length >= SEARCHABLE_FROM}
          required
          className="tw:max-w-md"
        />
      </div>
    </Card>
  )
}

function ColumnsCard({ columns }: { columns: CsvBoxColumn[] }) {
  return (
    <Card>
      <CardHeading icon="columns-03" title={t('columns.title')} subtitle={t('columns.subtitle')} />
      <ul aria-label={t('columns.title')} className="tw:flex tw:flex-col tw:divide-y tw:divide-gray-100 tw:px-5">
        {columns.map((column) => (
          <li
            key={column.column_name}
            className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-x-4 tw:gap-y-1 tw:py-2.5"
          >
            <span className="tw:text-sm tw:font-medium tw:text-gray-cool-900">{column.display_label}</span>
            <span className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
              {column.info_hint && <span className="tw:text-xs tw:text-gray-cool-600">{column.info_hint}</span>}
              {column.required && (
                <Badge color="muted" shape="rounded">
                  {t('columns.required')}
                </Badge>
              )}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function WhatHappensCard() {
  return (
    <Card>
      <CardHeading icon="info-circle" title={t('what_happens.title')} />
      <CheckList
        items={['each_row', 'values', 'tags'].map((key) => ({
          key,
          title: t(`what_happens.${key}_title`),
          body: t(`what_happens.${key}_body`),
        }))}
      />
    </Card>
  )
}

function YourFileCard({ groupName, columnCount }: { groupName: string | null; columnCount: number }) {
  const rows = [
    { label: t('your_file.group'), value: groupName ?? t('your_file.group_value') },
    { label: t('your_file.columns'), value: String(columnCount) },
    { label: t('your_file.format'), value: t('your_file.format_value') },
  ]

  // The template download lands with P2.6 (#8376).
  return (
    <Card className="tw:lg:sticky tw:lg:top-4">
      <CardHeading icon="file-05" title={t('your_file.title')} />
      <dl className="tw:flex tw:flex-col tw:gap-2 tw:p-5 tw:text-sm">
        {rows.map((row) => (
          <div key={row.label} className="tw:flex tw:items-baseline tw:justify-between tw:gap-4">
            <dt className="tw:text-gray-cool-600">{row.label}</dt>
            <dd className="tw:text-right tw:font-medium tw:text-gray-cool-900">{row.value}</dd>
          </div>
        ))}
      </dl>
    </Card>
  )
}

function ChecksCard({ scope, keys }: { scope: 'before_submit' | 'after_submit'; keys: string[] }) {
  return (
    <Card>
      <CardHeading icon="check-done-01" title={t(`${scope}.title`)} subtitle={t(`${scope}.subtitle`)} />
      <CheckList items={keys.map((key) => ({ key, body: t(`${scope}.${key}`) }))} />
    </Card>
  )
}

function ResultCard({ isSuccessful, busy, onReset }: { isSuccessful: boolean; busy: boolean; onReset: () => void }) {
  const icon: IconTypes = isSuccessful ? 'check-circle' : 'alert-circle'

  // The real import summary replaces this CSVBox boolean with P3.3 (#8379).
  return (
    <Card className="tw:mx-auto tw:w-full tw:max-w-xl">
      <div className="tw:flex tw:flex-col tw:items-center tw:gap-4 tw:px-6 tw:py-10 tw:text-center">
        <span
          aria-hidden
          className={cn(
            'tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-full',
            isSuccessful ? 'tw:bg-success-50 tw:text-success-600' : 'tw:bg-error-50 tw:text-error-600'
          )}
        >
          <Icon type={icon} className="tw:h-6 tw:w-6" />
        </span>
        <div className="tw:flex tw:flex-col tw:gap-1">
          <h2 className="tw:text-base tw:font-bold tw:text-gray-cool-900">
            {t(isSuccessful ? 'result.success_headline' : 'result.error_headline')}
          </h2>
          <p className="tw:text-sm tw:text-gray-cool-600">
            {t(isSuccessful ? 'result.success_content' : 'result.error_content')}
          </p>
        </div>
        <Button rank="secondary" leadingIcon="arrow-narrow-left" onClick={onReset} disabled={busy}>
          {busy ? t('group.opening') : t(isSuccessful ? 'result.import_another' : 'result.try_again')}
        </Button>
      </div>
    </Card>
  )
}

export default function SupporterImportsIndex({
  requestingUserId,
  groups,
  draftImport,
  licenseKey,
  csvBoxEnvironment,
  columns,
  pageTabs,
}: SupporterImportsIndexProps) {
  const [step, setStep] = useState(draftImport ? UPLOAD : PREPARE)
  const [isSuccessful, setIsSuccessful] = useState(false)
  const form = useForm({ action_group_id: draftImport ? String(draftImport.groupId) : '' })

  const groupId = form.data.action_group_id
  const groupName = groups.find((group) => String(group.id) === groupId)?.name ?? null

  // PageLayout.Root is the scrolling element here, not the window.
  const goToStep = (next: number) => {
    setStep(next)
    document.getElementById(ROOT_ID)?.scrollTo?.({ top: 0 })
    window.scrollTo({ top: 0 })
  }

  const chooseGroup = (value: string) => {
    form.setData('action_group_id', value)
    form.clearErrors('action_group_id')
  }

  // The draft is reused when the page already holds one; a queued one is left behind.
  const saveImport = () => {
    form.transform((data) => ({ ...data, import_id: draftImport?.id }))
    form.post(CREATE_IMPORT_PATH, { preserveState: true, onSuccess: () => goToStep(UPLOAD) })
  }

  const continueToUpload = () => {
    if (draftImport && String(draftImport.groupId) === groupId) {
      goToStep(UPLOAD)
    } else {
      saveImport()
    }
  }

  const finishImport = (result: boolean) => {
    setIsSuccessful(result)
    goToStep(CONFIRM)
  }

  return (
    <PageLayout.Root id={ROOT_ID}>
      <PageLayout.Header
        icon="users-01"
        heading={t('heading')}
        subtitle={t('subtitle')}
        nav={pageTabs && <PageHeaderNav ariaLabel={pageTabs.label} items={pageTabs.items} linkComponent={Link} />}
      />
      <PageLayout.Body>
        <div className="tw:mx-auto tw:flex tw:max-w-7xl tw:flex-col tw:gap-6">
          <div className="tw:py-2">
            <Steps steps={[t('steps.prepare'), t('steps.upload'), t('steps.confirm')]} currentStep={step} />
          </div>

          {step === PREPARE && (
            <div className="tw:grid tw:gap-6 tw:lg:grid-cols-[minmax(0,1fr)_20rem]">
              <div className="tw:flex tw:flex-col tw:gap-5">
                <GroupCard
                  groups={groups}
                  groupId={groupId}
                  onChange={chooseGroup}
                  error={form.errors.action_group_id}
                />
                <ColumnsCard columns={columns} />
                <WhatHappensCard />
              </div>
              <YourFileCard groupName={groupName} columnCount={columns.length} />
            </div>
          )}

          {step === UPLOAD && (
            <div className="tw:flex tw:flex-col tw:gap-5">
              {licenseKey ? (
                <Card>
                  <CardHeading icon="upload-cloud-02" title={t('upload.title')} subtitle={t('upload.subtitle')} />
                  <div className="tw:p-5">
                    <div className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:rounded-xl tw:border-2 tw:border-dashed tw:border-gray-cool-200 tw:bg-gray-cool-50/50 tw:px-6 tw:py-10 tw:text-center">
                      <span
                        aria-hidden
                        className="tw:flex tw:h-12 tw:w-12 tw:items-center tw:justify-center tw:rounded-full tw:bg-white tw:text-primary-600 tw:shadow-xs"
                      >
                        <Icon type="upload-cloud-02" className="tw:h-6 tw:w-6" />
                      </span>
                      <p className="tw:text-sm tw:font-medium tw:text-gray-cool-900">{t('upload.prompt')}</p>
                      <p className="tw:max-w-md tw:text-xs tw:text-gray-cool-600">
                        {t('upload.hint', { group: draftImport?.groupName })}
                      </p>
                      {draftImport && (
                        <SupporterImportCsvBox
                          requestingUserId={requestingUserId}
                          importToken={draftImport.token}
                          licenseKey={licenseKey}
                          environment={csvBoxEnvironment}
                          columns={columns}
                          onImport={finishImport}
                        />
                      )}
                    </div>
                  </div>
                </Card>
              ) : (
                <CsvBoxNotSetUpAlert />
              )}
              <div className="tw:grid tw:gap-5 tw:md:grid-cols-2">
                <ChecksCard scope="before_submit" keys={['match_columns', 'required_columns', 'fix_issues']} />
                <ChecksCard scope="after_submit" keys={['background']} />
              </div>
            </div>
          )}

          {step === CONFIRM && <ResultCard isSuccessful={isSuccessful} busy={form.processing} onReset={saveImport} />}
        </div>
      </PageLayout.Body>

      {step < CONFIRM && (
        <PageLayout.Footer>
          <div className="tw:mx-auto tw:flex tw:max-w-7xl tw:flex-wrap tw:items-center tw:justify-end tw:gap-3">
            {step === PREPARE ? (
              <Button size="xl" trailingIcon="arrow-narrow-right" onClick={continueToUpload} disabled={form.processing}>
                {form.processing ? t('group.opening') : t('continue')}
              </Button>
            ) : (
              <Button rank="secondary" onClick={() => goToStep(PREPARE)}>
                {t('back')}
              </Button>
            )}
          </div>
        </PageLayout.Footer>
      )}
    </PageLayout.Root>
  )
}
